'use client';

import React, { useEffect, useState } from "react";
import { NavButton } from '@/components/ui/button'; // Behalten, falls für ANMELDEN Button genutzt
import { signOut, useSession } from "next-auth/react";
import { useRouter } from 'next/navigation';
import useSWR from 'swr';
import { Session } from "next-auth";
import SearchModal from "@/components/ui/search-modal";
import { Grid, Search } from "flowbite-react-icons/outline";
import Image from 'next/image'
import DropdownProfile from "@/components/ui/dashboard/dropdown-profile";
import Link from "next/link"; // Behalten für andere Links
import LogoWortmarke from "@/components/ui/LogoWortmarke";
import LogoShort from "@/components/ui/LogoShort";
import Navigation from "@/components/ui/landing/Navigation";
import RotatingBanner from "@/components/ui/landing/RotatingBanner"; // Import the Navigation component directly

interface UserData {
  id: string;
  picture?: string;
  given_name?: string;
  family_name?: string;
  role?: string;
}

const useUserData = (session: Session | null) => {
  const fetcher = (url: string): Promise<UserData> => fetch(url).then((res) => res.json());
  const userId = session?.user?.id;
  const userRole = session?.user?.role;

  const { data, error, mutate } = useSWR<UserData>(
    userId ? `/api/${userRole?.toLowerCase()}/${userId}` : null,
    fetcher
  );

  return {
    userData: data,
    isLoading: !error && !data,
    isError: error,
    mutate
  };
};

export default function Header() {
  const [top, setTop] = useState<boolean>(true);
  const { data: session } = useSession();
  const router = useRouter();
  const { userData, isLoading, isError, mutate: mutateUserData } = useUserData(session);
  const [searchModalOpen, setSearchModalOpen] = useState(false);

  const handleSignOut = async (e: React.MouseEvent) => {
    e.preventDefault()
    await signOut({ callbackUrl: '/' })
  }

  // detect whether user has scrolled the page down by 10px
  const scrollHandler = () => {
    window.pageYOffset > 10 ? setTop(false) : setTop(true);
  };

  useEffect(() => {
    if (session?.user && !session.user.role) {
      handleIncompleteSession()
    }

    scrollHandler();
    window.addEventListener("scroll", scrollHandler);
    return () => window.removeEventListener("scroll", scrollHandler);
  }, [top, session?.user]);

  const handleIncompleteSession = async () => {
    await signOut({ redirect: false });
  };

  return (
    <header className="fixed z-30 w-full pt-0 bg-white" style={{ paddingRight: 'var(--scrollbar-width, 0px)' }}>
      {/* Top Banner */}
      <RotatingBanner />

      {/* Main Header */}
      <div className="mx-auto px-4 sm:px-6">
        <div className="relative flex h-14 items-center justify-between gap-3 bg-white px-3">

          {/* Site branding and mobile nav toggle */}
          <div className="flex items-center gap-2 mb-2 -ml-3 mt-4">
            {/* Mobile hamburger menu - placed before logo */}
            <div className="lg:hidden">
              {/* --- Prop für mobilen Menüpunkt übergeben --- */}
              <Navigation showGuideLinkInMobileMenu={!session} />
            </div>

            {/* Mobile: Show LogoShort */}
            <div className="md:hidden">
              <LogoShort size={40} />
            </div>

            {/* Desktop: Show LogoWortmarke */}
            <div className="hidden md:block">
              <LogoWortmarke size={40} />
            </div>
          </div>

          {/* Desktop Navigation - hidden on mobile */}
          <div className="hidden lg:block flex-1">
            {/* Desktop navigation braucht die Prop nicht */}
            <Navigation className="flex justify-center" />
          </div>

          {/* Right side icons/buttons */}
          <ul className="flex items-center justify-end gap-3">
            {/* Search button */}
            <li className="relative">
              <div>
                <button
                  className={`w-9 h-9 flex items-center justify-center hover:bg-gray-100 lg:hover:bg-gray-200 dark:hover:bg-gray-700/50 dark:lg:hover:bg-gray-800 rounded-full ${searchModalOpen && 'bg-gray-200 dark:bg-gray-800'}`}
                  onClick={() => {
                    setSearchModalOpen(true)
                  }}
                  type="button"
                >
                  <span className="sr-only">Suche</span>
                  <Search className="text-primary-500/80 dark:text-primary-400/80" />
                </button>
                <SearchModal isOpen={searchModalOpen} setIsOpen={setSearchModalOpen} />
              </div>
            </li>

            {/* --- Guide werden Button HIER KOMPLETT ENTFERNT --- */}

            {/* Conditional rendering based on session */}
            {session ? (
              <>
                {/* Dashboard Button / Icon */}
                <li className="relative md:block">
                  <button
                    onClick={() => router.push('/dashboard/customer')}
                    className="rounded-full text-primary hover:text-primary-dark border-2 border-primary hover:border-primary-dark inline-flex items-center justify-center px-3 h-8 pt-1 text-sm font-bold uppercase transition-colors duration-300 bg-white leading-none md:block hidden" // Hidden on mobile
                    type="button"
                  >
                    Mein Konto
                  </button>
                  {/* Mobile dashboard icon */}
                  <button
                    onClick={() => router.push('/dashboard/customer')}
                    className="md:hidden rounded-full border-2 border-primary w-8 h-8 flex items-center justify-center" // Shown only on mobile
                    type="button"
                  >
                    <Grid className="w-5 h-5 text-primary" />
                  </button>
                </li>

                {/* Profile Dropdown / Sign Out Button */}
                <li>
                  {/* Mobile View: Dropdown */}
                  <div className="md:hidden mt-3">
                    {userData && (
                      <DropdownProfile
                        align="right"
                        picture={userData.picture || session?.user?.picture || "/images/logo.png"}
                        givenName={userData.given_name as string}
                        familyName={userData.family_name as string}
                        role={userData.role as string}
                      />
                    )}
                  </div>

                  {/* Desktop View: Sign Out Button */}
                  <button
                    onClick={handleSignOut}
                    className="hidden md:inline-flex rounded-full text-primary hover:text-primary-dark border-2
                      border-primary hover:border-primary-dark items-center justify-center
                      px-3 h-8 text-sm font-bold uppercase transition-colors duration-300 bg-white leading-none gap-2" // Hidden on mobile
                    type="button"
                  >
                    <Image
                      className="w-5 h-5 rounded-full object-cover"
                      src={userData?.picture || session?.user?.picture || "/images/logo.png"}
                      width={32}
                      height={32}
                      alt="User"
                    />
                    <span className="mt-1">Abmelden</span>
                  </button>
                </li>
              </>
            ) : (
              <>
                {/* Login Button (if not logged in) */}
                <li className="relative">
                  <NavButton href="/auth/signin">
                    ANMELDEN
                  </NavButton>
                </li>
              </>
            )}
          </ul>
        </div>
      </div>
    </header>
  );
}